import { computed, ref } from 'vue'
import { defineStore } from 'pinia'

import {
  AssayEnvelopeBrowserService,
  type AssayEnvelopeGroupOut,
  type ExtractionAssignmentOut,
  type ExtractionReviewerOut,
} from '@/api'
import {
  DEFAULT_EXTRACTIONS_QUERY,
  DEFAULT_TREE_KIND,
  type ExtractionsQuery,
  type NativeExtractionQueueRow,
  type TreeKind,
} from '@/types/extractions'
import { classifyApiError, extractErrorMessage } from '@/utils/apiError'

function projectKey(projectId: number): string {
  return String(projectId)
}

function listKey(projectId: number, treeKind: TreeKind): string {
  return `${projectId}:${treeKind}`
}

function listKeysForProject(projectId: number): string[] {
  return [listKey(projectId, 'assay'), listKey(projectId, 'channel')]
}

function groupKey(projectId: number, treeKind: TreeKind, scopeId: string): string {
  return `${projectId}:${treeKind}:${scopeId}`
}

function normalizeFetchArgs(
  treeKindOrOverride: TreeKind | Partial<ExtractionsQuery> = DEFAULT_TREE_KIND,
  override: Partial<ExtractionsQuery> = {},
): { treeKind: TreeKind; override: Partial<ExtractionsQuery> } {
  if (treeKindOrOverride === 'assay' || treeKindOrOverride === 'channel') {
    return { treeKind: treeKindOrOverride, override }
  }
  return { treeKind: DEFAULT_TREE_KIND, override: treeKindOrOverride }
}

const GROUP_CACHE_LIMIT = 500

interface AssignmentRecoveryState {
  message: string
  controlsDisabled: boolean
}

export interface AssignmentMutationResult {
  status: 'saved' | 'saved_refresh_failed' | 'conflict_refreshed' | 'ownership_unknown'
  message?: string
}

type AssignmentRefreshOutcome = 'refreshed' | 'superseded' | 'failed'

export function envelopeGroupPresentation(group: AssayEnvelopeGroupOut): NativeExtractionQueueRow {
  return {
    id: group.scope_id,
    project_id: group.project_id,
    source_file_paths: group.files.map((file) => file.source_path),
    source_commit_sha: group.source_commit_sha ?? '',
    name: group.display_name,
    lifecycle_version: group.lifecycle_version,
    status: 'success',
    artifacts: group.files.map((file) => ({
      file_id: file.file_id,
      file_url: file.source_path,
      source_file_path: file.source_path,
      marked_done: file.marked_done,
    })),
    extractor_format: group.extractor_format,
    tree_kind: group.tree_kind ?? DEFAULT_TREE_KIND,
    origin: group.origin,
    import_commit_sha: group.import_commit_sha,
    started_at: group.started_at,
    completed_at: group.updated_at,
    created_at: group.created_at,
    review_total_count: group.review_total_count,
    review_marked_done_count: group.review_marked_done_count,
    review_state: group.review_state,
    assignment: group.assignment,
  }
}

export const useAssayEnvelopeExtractionsStore = defineStore('assayEnvelopeExtractions', () => {
  const availabilityByProject = ref<Map<string, boolean>>(new Map())
  const availabilityReasonByProject = ref<Map<string, string | null>>(new Map())
  const groupsByProject = ref<Map<string, NativeExtractionQueueRow[]>>(new Map())
  const groupDetails = ref<Map<string, NativeExtractionQueueRow>>(new Map())
  const sourceGroups = ref<Map<string, AssayEnvelopeGroupOut>>(new Map())
  const totalByProject = ref<Map<string, number>>(new Map())
  const queryByProject = ref<Map<string, ExtractionsQuery>>(new Map())
  const preparedQueryByProject = new Map<string, string>()
  const loadingByProject = ref<Map<string, boolean>>(new Map())
  const errorByProject = ref<Map<string, string | null>>(new Map())
  const reviewersByProject = ref<Map<string, ExtractionReviewerOut[]>>(new Map())
  const reviewerLoadingByProject = ref<Map<string, boolean>>(new Map())
  const reviewerErrorByProject = ref<Map<string, string | null>>(new Map())
  const reviewerLoadedProjects = new Set<string>()
  const reviewerGeneration = new Map<string, number>()
  const assignmentPending = ref<Map<string, number>>(new Map())
  const assignmentRecovery = ref<Map<string, AssignmentRecoveryState>>(new Map())
  const focusedDetailKeys = new Set<string>()
  const availabilityGeneration = new Map<string, number>()
  const listGeneration = new Map<string, number>()
  const detailGeneration = new Map<string, number>()
  const groupWriteGeneration = new Map<string, number>()
  let cacheEpoch = 0
  let requestGeneration = 0

  function cacheGroup(
    key: string,
    source: AssayEnvelopeGroupOut,
    presentation: NativeExtractionQueueRow,
  ): void {
    sourceGroups.value.delete(key)
    groupDetails.value.delete(key)
    sourceGroups.value.set(key, source)
    groupDetails.value.set(key, presentation)
    while (groupDetails.value.size > GROUP_CACHE_LIMIT) {
      const oldestKey = groupDetails.value.keys().next().value
      if (!oldestKey) break
      groupDetails.value.delete(oldestKey)
      sourceGroups.value.delete(oldestKey)
      detailGeneration.delete(oldestKey)
      groupWriteGeneration.delete(oldestKey)
      focusedDetailKeys.delete(oldestKey)
    }
  }

  function reset(): void {
    cacheEpoch += 1
    availabilityByProject.value.clear()
    availabilityReasonByProject.value.clear()
    groupsByProject.value.clear()
    groupDetails.value.clear()
    sourceGroups.value.clear()
    totalByProject.value.clear()
    queryByProject.value.clear()
    preparedQueryByProject.clear()
    loadingByProject.value.clear()
    errorByProject.value.clear()
    reviewersByProject.value.clear()
    reviewerLoadingByProject.value.clear()
    reviewerErrorByProject.value.clear()
    reviewerLoadedProjects.clear()
    reviewerGeneration.clear()
    assignmentPending.value.clear()
    assignmentRecovery.value.clear()
    focusedDetailKeys.clear()
    availabilityGeneration.clear()
    listGeneration.clear()
    detailGeneration.clear()
    groupWriteGeneration.clear()
  }

  function availableFor(projectId: number) {
    return computed(() => availabilityByProject.value.get(projectKey(projectId)) ?? false)
  }

  function availabilityKnownFor(projectId: number) {
    return computed(() => availabilityByProject.value.has(projectKey(projectId)))
  }

  function availabilityReasonFor(projectId: number) {
    return computed(() => availabilityReasonByProject.value.get(projectKey(projectId)) ?? null)
  }

  async function refreshAvailability(projectId: number): Promise<boolean> {
    const epoch = cacheEpoch
    const key = projectKey(projectId)
    const generation = (availabilityGeneration.get(key) ?? 0) + 1
    availabilityGeneration.set(key, generation)
    try {
      const result =
        await AssayEnvelopeBrowserService.getAvailabilityProjectsProjectIdAssayEnvelopesAvailabilityGet(
          { projectId },
        )
      if (epoch !== cacheEpoch || availabilityGeneration.get(key) !== generation) return false
      availabilityByProject.value.set(key, result.available)
      availabilityReasonByProject.value.set(key, result.reason ?? null)
      if (!result.available) clearProject(projectId)
      return result.available
    } catch (error) {
      if (epoch === cacheEpoch && availabilityGeneration.get(key) === generation) {
        availabilityByProject.value.set(key, false)
        availabilityReasonByProject.value.set(key, extractErrorMessage(error))
        clearProject(projectId)
      }
      throw error
    }
  }

  function clearProject(projectId: number): void {
    for (const key of listKeysForProject(projectId)) {
      listGeneration.set(key, (listGeneration.get(key) ?? 0) + 1)
      groupsByProject.value.delete(key)
      totalByProject.value.delete(key)
      queryByProject.value.delete(key)
      preparedQueryByProject.delete(key)
      loadingByProject.value.delete(key)
      errorByProject.value.delete(key)
    }
    clearProjectCaches(projectId)
    clearProjectAssignmentState(projectId)
  }

  function clearProjectCaches(projectId: number): void {
    const prefix = `${projectId}:`
    for (const cacheKey of [...groupDetails.value.keys()]) {
      if (cacheKey.startsWith(prefix)) groupDetails.value.delete(cacheKey)
    }
    for (const cacheKey of [...sourceGroups.value.keys()]) {
      if (cacheKey.startsWith(prefix)) sourceGroups.value.delete(cacheKey)
    }
    for (const cacheKey of [...detailGeneration.keys()]) {
      if (!cacheKey.startsWith(prefix)) continue
      detailGeneration.set(cacheKey, (detailGeneration.get(cacheKey) ?? 0) + 1)
    }
    for (const cacheKey of [...groupWriteGeneration.keys()]) {
      if (cacheKey.startsWith(prefix)) groupWriteGeneration.delete(cacheKey)
    }
  }

  function clearProjectAssignmentState(projectId: number): void {
    const prefix = `${projectId}:`
    const key = projectKey(projectId)
    reviewerGeneration.set(key, (reviewerGeneration.get(key) ?? 0) + 1)
    reviewersByProject.value.delete(key)
    reviewerLoadingByProject.value.delete(key)
    reviewerErrorByProject.value.delete(key)
    reviewerLoadedProjects.delete(key)
    for (const cacheKey of [...assignmentPending.value.keys()]) {
      if (cacheKey.startsWith(prefix)) assignmentPending.value.delete(cacheKey)
    }
    for (const cacheKey of [...assignmentRecovery.value.keys()]) {
      if (cacheKey.startsWith(prefix)) assignmentRecovery.value.delete(cacheKey)
    }
    for (const cacheKey of [...focusedDetailKeys]) {
      if (cacheKey.startsWith(prefix)) focusedDetailKeys.delete(cacheKey)
    }
  }

  function extractionsFor(projectId: number, treeKind: TreeKind = DEFAULT_TREE_KIND) {
    return computed(() => groupsByProject.value.get(listKey(projectId, treeKind)) ?? [])
  }

  function totalFor(projectId: number, treeKind: TreeKind = DEFAULT_TREE_KIND) {
    return computed(() => totalByProject.value.get(listKey(projectId, treeKind)) ?? 0)
  }

  function queryFor(projectId: number, treeKind: TreeKind = DEFAULT_TREE_KIND) {
    return computed(
      () =>
        queryByProject.value.get(listKey(projectId, treeKind)) ?? { ...DEFAULT_EXTRACTIONS_QUERY },
    )
  }

  function loadingFor(projectId: number, treeKind: TreeKind = DEFAULT_TREE_KIND) {
    return computed(() => loadingByProject.value.get(listKey(projectId, treeKind)) ?? false)
  }

  function markPreparedList(projectId: number, treeKind: TreeKind, query: ExtractionsQuery): void {
    preparedQueryByProject.set(listKey(projectId, treeKind), JSON.stringify(query))
  }

  function consumePreparedList(
    projectId: number,
    treeKind: TreeKind,
    query: ExtractionsQuery,
  ): boolean {
    const key = listKey(projectId, treeKind)
    const prepared = preparedQueryByProject.get(key)
    preparedQueryByProject.delete(key)
    return prepared === JSON.stringify(query)
  }

  function errorFor(projectId: number, treeKind: TreeKind = DEFAULT_TREE_KIND) {
    return computed(() => errorByProject.value.get(listKey(projectId, treeKind)) ?? null)
  }

  function reviewersFor(projectId: number) {
    return computed(() => reviewersByProject.value.get(projectKey(projectId)) ?? [])
  }

  function reviewerLoadingFor(projectId: number) {
    return computed(() => reviewerLoadingByProject.value.get(projectKey(projectId)) ?? false)
  }

  function reviewerErrorFor(projectId: number) {
    return computed(() => reviewerErrorByProject.value.get(projectKey(projectId)) ?? null)
  }

  function assignmentPendingFor(
    projectId: number,
    scopeId: string,
    treeKind: TreeKind = DEFAULT_TREE_KIND,
  ) {
    return computed(() => assignmentPending.value.has(groupKey(projectId, treeKind, scopeId)))
  }

  function assignmentRecoveryFor(
    projectId: number,
    scopeId: string,
    treeKind: TreeKind = DEFAULT_TREE_KIND,
  ) {
    return computed(
      () => assignmentRecovery.value.get(groupKey(projectId, treeKind, scopeId)) ?? null,
    )
  }

  function extractionById(
    projectId: number,
    scopeId: string,
    treeKind: TreeKind = DEFAULT_TREE_KIND,
  ) {
    return computed(() => groupDetails.value.get(groupKey(projectId, treeKind, scopeId)) ?? null)
  }

  function groupFilesFor(
    projectId: number,
    scopeId: string,
    treeKind: TreeKind = DEFAULT_TREE_KIND,
  ) {
    return computed(
      () => sourceGroups.value.get(groupKey(projectId, treeKind, scopeId))?.files ?? [],
    )
  }

  function contributingExtractionIdFor(
    projectId: number,
    scopeId: string,
    treeKind: TreeKind = DEFAULT_TREE_KIND,
  ): string | null {
    return (
      sourceGroups.value.get(groupKey(projectId, treeKind, scopeId))?.contributing_extraction_id ??
      null
    )
  }

  function contributingLineageFor(
    projectId: number,
    scopeId: string,
    fileId: string,
    treeKind: TreeKind = DEFAULT_TREE_KIND,
  ): { extractionId: string; certSlug: string } | null {
    const file = sourceGroups.value
      .get(groupKey(projectId, treeKind, scopeId))
      ?.files.find((candidate) => candidate.file_id === fileId)
    if (!file?.contributing_extraction_id || !file.contributing_cert_slug) return null
    return {
      extractionId: file.contributing_extraction_id,
      certSlug: file.contributing_cert_slug,
    }
  }

  async function fetchExtractions(
    projectId: number,
    treeKindOrOverride: TreeKind | Partial<ExtractionsQuery> = DEFAULT_TREE_KIND,
    maybeOverride: Partial<ExtractionsQuery> = {},
  ): Promise<boolean> {
    const { treeKind, override } = normalizeFetchArgs(treeKindOrOverride, maybeOverride)
    const epoch = cacheEpoch
    const key = listKey(projectId, treeKind)
    preparedQueryByProject.delete(key)
    const current = {
      ...DEFAULT_EXTRACTIONS_QUERY,
      ...(queryByProject.value.get(key) ?? {}),
      ...override,
    }
    queryByProject.value.set(key, current)
    const generation = (listGeneration.get(key) ?? 0) + 1
    const writeGeneration = ++requestGeneration
    listGeneration.set(key, generation)
    loadingByProject.value.set(key, true)
    errorByProject.value.set(key, null)
    try {
      const response =
        await AssayEnvelopeBrowserService.listGroupsProjectsProjectIdAssayEnvelopesGroupsGet({
          projectId,
          search: current.search,
          review: current.review,
          assignee: current.assignee,
          assigneeIds: current.assigneeIds,
          includeUnassigned: current.includeUnassigned,
          sort: current.sort,
          page: current.page,
          pageSize: current.pageSize,
          treeKind,
        })
      if (epoch !== cacheEpoch || listGeneration.get(key) !== generation) return false
      const presentations = response.items.map((group) => {
        const cacheKey = groupKey(projectId, treeKind, group.scope_id)
        if ((groupWriteGeneration.get(cacheKey) ?? 0) > writeGeneration) {
          return groupDetails.value.get(cacheKey) ?? envelopeGroupPresentation(group)
        }
        groupWriteGeneration.set(cacheKey, writeGeneration)
        const presentation = envelopeGroupPresentation(group)
        cacheGroup(cacheKey, group, presentation)
        return presentation
      })
      groupsByProject.value.set(key, presentations)
      totalByProject.value.set(key, response.total)
      return true
    } catch (error) {
      if (epoch !== cacheEpoch || listGeneration.get(key) !== generation) return false
      const { status } = classifyApiError(error)
      if (status !== undefined && status >= 400 && status < 500) clearProject(projectId)
      errorByProject.value.set(key, extractErrorMessage(error))
      return false
    } finally {
      if (epoch === cacheEpoch && listGeneration.get(key) === generation) {
        loadingByProject.value.set(key, false)
      }
    }
  }

  async function fetchExtractionByIdOrThrow(
    projectId: number,
    scopeId: string,
    treeKind: TreeKind = DEFAULT_TREE_KIND,
  ): Promise<NativeExtractionQueueRow> {
    const epoch = cacheEpoch
    const key = groupKey(projectId, treeKind, scopeId)
    const generation = (detailGeneration.get(key) ?? 0) + 1
    const writeGeneration = ++requestGeneration
    detailGeneration.set(key, generation)
    groupWriteGeneration.set(key, writeGeneration)
    const group =
      await AssayEnvelopeBrowserService.getGroupProjectsProjectIdAssayEnvelopesGroupsScopeIdGet({
        projectId,
        scopeId,
        treeKind,
      })
    if (
      epoch !== cacheEpoch ||
      detailGeneration.get(key) !== generation ||
      (groupWriteGeneration.get(key) ?? 0) > writeGeneration
    ) {
      throw new Error('The extraction request was superseded by newer navigation.')
    }
    const presentation = envelopeGroupPresentation(group)
    groupWriteGeneration.set(key, writeGeneration)
    cacheGroup(key, group, presentation)
    focusedDetailKeys.add(key)
    return presentation
  }

  async function fetchReviewers(
    projectId: number,
    forceRefresh = false,
  ): Promise<ExtractionReviewerOut[]> {
    const epoch = cacheEpoch
    const key = projectKey(projectId)
    if (!forceRefresh && reviewerLoadedProjects.has(key)) {
      return reviewersByProject.value.get(key) ?? []
    }
    if (forceRefresh) {
      reviewersByProject.value.delete(key)
      reviewerLoadedProjects.delete(key)
    }
    const generation = (reviewerGeneration.get(key) ?? 0) + 1
    reviewerGeneration.set(key, generation)
    reviewerLoadingByProject.value.set(key, true)
    reviewerErrorByProject.value.set(key, null)
    try {
      const reviewers =
        await AssayEnvelopeBrowserService.listReviewersProjectsProjectIdAssayEnvelopesReviewersGet({
          projectId,
        })
      if (epoch !== cacheEpoch || reviewerGeneration.get(key) !== generation) return []
      reviewersByProject.value.set(key, reviewers)
      reviewerLoadedProjects.add(key)
      return reviewers
    } catch (error) {
      if (epoch !== cacheEpoch || reviewerGeneration.get(key) !== generation) return []
      reviewerErrorByProject.value.set(key, extractErrorMessage(error))
      throw error
    } finally {
      if (epoch === cacheEpoch && reviewerGeneration.get(key) === generation) {
        reviewerLoadingByProject.value.set(key, false)
      }
    }
  }

  function applyAssignmentToCache(
    projectId: number,
    scopeId: string,
    treeKind: TreeKind,
    assignment: ExtractionAssignmentOut,
  ): void {
    const key = groupKey(projectId, treeKind, scopeId)
    const writeGeneration = ++requestGeneration
    groupWriteGeneration.set(key, writeGeneration)
    const source = sourceGroups.value.get(key)
    if (source) sourceGroups.value.set(key, { ...source, assignment })
    const detail = groupDetails.value.get(key)
    if (detail) groupDetails.value.set(key, { ...detail, assignment })
    const listCacheKey = listKey(projectId, treeKind)
    const rows = groupsByProject.value.get(listCacheKey)
    if (rows) {
      groupsByProject.value.set(
        listCacheKey,
        rows.map((row) => (row.id === scopeId ? { ...row, assignment } : row)),
      )
    }
  }

  function applyLifecycleToCache(
    projectId: number,
    scopeId: string,
    treeKind: TreeKind,
    effectiveDisplayName: string,
    version: number,
  ): void {
    const key = groupKey(projectId, treeKind, scopeId)
    groupWriteGeneration.set(key, ++requestGeneration)
    const source = sourceGroups.value.get(key)
    if (source) {
      sourceGroups.value.set(key, {
        ...source,
        display_name: effectiveDisplayName,
        lifecycle_version: version,
      })
    }
    const detail = groupDetails.value.get(key)
    if (detail) {
      groupDetails.value.set(key, {
        ...detail,
        name: effectiveDisplayName,
        lifecycle_version: version,
      })
    }
    const listCacheKey = listKey(projectId, treeKind)
    const rows = groupsByProject.value.get(listCacheKey)
    if (rows) {
      groupsByProject.value.set(
        listCacheKey,
        rows.map((row) =>
          row.id === scopeId
            ? { ...row, name: effectiveDisplayName, lifecycle_version: version }
            : row,
        ),
      )
    }
  }

  function removeGroupFromCache(projectId: number, scopeId: string, treeKind: TreeKind): void {
    const key = groupKey(projectId, treeKind, scopeId)
    groupWriteGeneration.set(key, ++requestGeneration)
    groupDetails.value.delete(key)
    sourceGroups.value.delete(key)
    focusedDetailKeys.delete(key)
    const listCacheKey = listKey(projectId, treeKind)
    const rows = groupsByProject.value.get(listCacheKey)
    if (rows?.some((row) => row.id === scopeId)) {
      groupsByProject.value.set(
        listCacheKey,
        rows.filter((row) => row.id !== scopeId),
      )
      totalByProject.value.set(
        listCacheKey,
        Math.max(0, (totalByProject.value.get(listCacheKey) ?? 1) - 1),
      )
    }
  }

  async function refreshActiveList(
    projectId: number,
    treeKind: TreeKind,
  ): Promise<AssignmentRefreshOutcome> {
    const epoch = cacheEpoch
    const key = listKey(projectId, treeKind)
    const current = queryByProject.value.get(key) ?? { ...DEFAULT_EXTRACTIONS_QUERY }
    const expectedGeneration = (listGeneration.get(key) ?? 0) + 1
    if (!(await fetchExtractions(projectId, treeKind, current))) {
      if (
        epoch !== cacheEpoch ||
        (listGeneration.get(key) !== expectedGeneration && !errorByProject.value.get(key))
      ) {
        return 'superseded'
      }
      return 'failed'
    }
    const total = totalByProject.value.get(key) ?? 0
    const maximumPage = Math.max(1, Math.ceil(total / current.pageSize))
    if (current.page <= maximumPage) return 'refreshed'

    const expectedClampGeneration = (listGeneration.get(key) ?? 0) + 1
    if (!(await fetchExtractions(projectId, treeKind, { page: maximumPage }))) {
      if (
        epoch !== cacheEpoch ||
        (listGeneration.get(key) !== expectedClampGeneration && !errorByProject.value.get(key))
      ) {
        return 'superseded'
      }
      return 'failed'
    }
    return 'refreshed'
  }

  async function refreshAssignmentState(
    projectId: number,
    scopeId: string,
    treeKind: TreeKind,
  ): Promise<AssignmentRefreshOutcome> {
    const epoch = cacheEpoch
    const listOutcome = await refreshActiveList(projectId, treeKind)
    if (listOutcome !== 'refreshed') return listOutcome
    const detailKey = groupKey(projectId, treeKind, scopeId)
    if (focusedDetailKeys.has(detailKey)) {
      const expectedDetailGeneration = (detailGeneration.get(detailKey) ?? 0) + 1
      try {
        await fetchExtractionByIdOrThrow(projectId, scopeId, treeKind)
      } catch {
        if (epoch !== cacheEpoch || detailGeneration.get(detailKey) !== expectedDetailGeneration) {
          return 'superseded'
        }
        return 'failed'
      }
    }
    return 'refreshed'
  }

  async function recoverConflictedAssignment(
    projectId: number,
    scopeId: string,
    treeKind: TreeKind,
  ): Promise<boolean> {
    const epoch = cacheEpoch
    const key = groupKey(projectId, treeKind, scopeId)
    const generation = (detailGeneration.get(key) ?? 0) + 1
    const writeGeneration = ++requestGeneration
    detailGeneration.set(key, generation)
    groupWriteGeneration.set(key, writeGeneration)
    try {
      const group =
        await AssayEnvelopeBrowserService.getGroupProjectsProjectIdAssayEnvelopesGroupsScopeIdGet({
          projectId,
          scopeId,
          treeKind,
        })
      if (
        epoch !== cacheEpoch ||
        detailGeneration.get(key) !== generation ||
        (groupWriteGeneration.get(key) ?? 0) > writeGeneration
      ) {
        return false
      }
      const presentation = envelopeGroupPresentation(group)
      groupWriteGeneration.set(key, writeGeneration)
      cacheGroup(key, group, presentation)
      const rows = groupsByProject.value.get(listKey(projectId, treeKind))
      if (rows) {
        groupsByProject.value.set(
          listKey(projectId, treeKind),
          rows.map((row) => (row.id === scopeId ? presentation : row)),
        )
      }
      const listOutcome = await refreshActiveList(projectId, treeKind)
      if (listOutcome === 'failed') {
        groupsByProject.value.delete(listKey(projectId, treeKind))
        totalByProject.value.delete(listKey(projectId, treeKind))
      }
      return true
    } catch {
      return false
    }
  }

  async function updateAssignment(
    projectId: number,
    scopeId: string,
    treeKind: TreeKind,
    assigneeUserId: string | null,
    expectedVersion: number,
  ): Promise<AssignmentMutationResult> {
    const epoch = cacheEpoch
    const key = groupKey(projectId, treeKind, scopeId)
    if (assignmentPending.value.has(key)) {
      throw new Error('This assignment update is already in progress.')
    }
    const pendingToken = ++requestGeneration
    assignmentPending.value.set(key, pendingToken)
    assignmentRecovery.value.delete(key)
    try {
      const assignment =
        await AssayEnvelopeBrowserService.updateGroupAssignmentProjectsProjectIdAssayEnvelopesGroupsScopeIdAssignmentPut(
          {
            projectId,
            scopeId,
            treeKind,
            requestBody: {
              assignee_user_id: assigneeUserId,
              expected_version: expectedVersion,
            },
          },
        )
      if (epoch !== cacheEpoch) return { status: 'saved' }
      applyAssignmentToCache(projectId, scopeId, treeKind, assignment)
      const refreshOutcome = await refreshAssignmentState(projectId, scopeId, treeKind)
      if (refreshOutcome !== 'failed') return { status: 'saved' }
      const message = 'Assignment saved, but the queue could not refresh.'
      assignmentRecovery.value.set(key, { message, controlsDisabled: true })
      return { status: 'saved_refresh_failed', message }
    } catch (error) {
      if (epoch !== cacheEpoch) throw error
      const normalized = classifyApiError(error)
      if (normalized.code === 'extraction_assignee_ineligible') {
        try {
          await fetchReviewers(projectId, true)
        } catch {
          // Preserve the assignment failure; reviewerErrorByProject records the refresh failure.
        }
        throw error
      }
      if (normalized.status !== 409 || normalized.code !== 'extraction_assignment_conflict') {
        throw error
      }
      if (await recoverConflictedAssignment(projectId, scopeId, treeKind)) {
        const message = normalized.message
        assignmentRecovery.value.set(key, { message, controlsDisabled: false })
        return { status: 'conflict_refreshed', message }
      }
      if (epoch !== cacheEpoch) throw error
      const message = 'This assignment changed, but current ownership is unknown. Retry refresh.'
      assignmentRecovery.value.set(key, { message, controlsDisabled: true })
      return { status: 'ownership_unknown', message }
    } finally {
      if (assignmentPending.value.get(key) === pendingToken) assignmentPending.value.delete(key)
    }
  }

  async function retryAssignmentRefresh(
    projectId: number,
    scopeId: string,
    treeKind: TreeKind,
  ): Promise<boolean> {
    const epoch = cacheEpoch
    const key = groupKey(projectId, treeKind, scopeId)
    if (assignmentPending.value.has(key)) return false
    const pendingToken = ++requestGeneration
    assignmentPending.value.set(key, pendingToken)
    try {
      const outcome = await refreshAssignmentState(projectId, scopeId, treeKind)
      if (
        outcome === 'refreshed' &&
        epoch === cacheEpoch &&
        assignmentPending.value.get(key) === pendingToken
      ) {
        assignmentRecovery.value.delete(key)
      }
      return outcome === 'refreshed'
    } finally {
      if (assignmentPending.value.get(key) === pendingToken) assignmentPending.value.delete(key)
    }
  }

  async function refreshLifecycleState(
    projectId: number,
    scopeId: string,
    treeKind: TreeKind,
  ): Promise<void> {
    try {
      const lifecycle =
        await AssayEnvelopeBrowserService.getGroupLifecycleProjectsProjectIdAssayEnvelopesGroupsScopeIdLifecycleGet(
          { projectId, scopeId, treeKind },
        )
      if (lifecycle.is_deleted) {
        removeGroupFromCache(projectId, scopeId, treeKind)
      } else {
        applyLifecycleToCache(
          projectId,
          scopeId,
          treeKind,
          lifecycle.effective_display_name,
          lifecycle.version,
        )
      }
    } catch {
      removeGroupFromCache(projectId, scopeId, treeKind)
    }
    await refreshActiveList(projectId, treeKind)
  }

  async function renameGroup(
    projectId: number,
    scopeId: string,
    treeKind: TreeKind,
    displayName: string | null,
    expectedVersion: number,
  ): Promise<string | null> {
    try {
      const lifecycle =
        await AssayEnvelopeBrowserService.renameGroupProjectsProjectIdAssayEnvelopesGroupsScopeIdLifecyclePatch(
          {
            projectId,
            scopeId,
            treeKind,
            requestBody: {
              display_name: displayName,
              expected_version: expectedVersion,
            },
          },
        )
      applyLifecycleToCache(
        projectId,
        scopeId,
        treeKind,
        lifecycle.effective_display_name,
        lifecycle.version,
      )
      await refreshActiveList(projectId, treeKind)
      return null
    } catch (error) {
      if (classifyApiError(error).status !== 409) throw error
      await refreshLifecycleState(projectId, scopeId, treeKind)
      return extractErrorMessage(error)
    }
  }

  async function deleteGroup(
    projectId: number,
    scopeId: string,
    treeKind: TreeKind,
    expectedVersion: number,
  ): Promise<string | null> {
    try {
      await AssayEnvelopeBrowserService.deleteGroupProjectsProjectIdAssayEnvelopesGroupsScopeIdLifecycleDelete(
        { projectId, scopeId, treeKind, expectedVersion },
      )
      removeGroupFromCache(projectId, scopeId, treeKind)
      await refreshActiveList(projectId, treeKind)
      return null
    } catch (error) {
      if (classifyApiError(error).status !== 409) throw error
      await refreshLifecycleState(projectId, scopeId, treeKind)
      return extractErrorMessage(error)
    }
  }

  return {
    availableFor,
    availabilityKnownFor,
    availabilityReasonFor,
    extractionsFor,
    totalFor,
    queryFor,
    loadingFor,
    markPreparedList,
    consumePreparedList,
    errorFor,
    reviewersFor,
    reviewerLoadingFor,
    reviewerErrorFor,
    assignmentPendingFor,
    assignmentRecoveryFor,
    extractionById,
    groupFilesFor,
    contributingExtractionIdFor,
    contributingLineageFor,
    refreshAvailability,
    fetchExtractions,
    fetchExtractionByIdOrThrow,
    fetchReviewers,
    updateAssignment,
    retryAssignmentRefresh,
    renameGroup,
    deleteGroup,
    reset,
  }
})
